import { z } from 'zod';
import { gameSessionChatInitShape, gameSessionChatMessageShape, } from './chat.js';
import { colorNames } from './colors.js';
import { idShapes } from './ids.js';
import { gameSessionPlayerStatusShape, gameSessionPlayerStatusUpdateShape, gameStatusShape, } from './status.js';
const baseServerMessageShape = z.object({
    responseTo: z.string().optional().nullable(),
});
export const serverPongMessageShape = baseServerMessageShape.extend({
    type: z.literal('pong'),
});
export const serverPlayerStatusChangeMessageShape = baseServerMessageShape.extend({
    type: z.literal('playerStatusChange'),
    playerId: idShapes.User,
    playerStatus: gameSessionPlayerStatusUpdateShape,
});
export const serverChatMessageShape = baseServerMessageShape.extend({
    type: z.literal('chat'),
    messages: gameSessionChatMessageShape.array(),
    sceneId: z.string().nullable(),
    nextToken: z.string().nullable().optional(),
});
export const serverRoundChangeMessageShape = baseServerMessageShape.extend({
    type: z.literal('roundChange'),
    playerStatuses: z.record(idShapes.User, gameSessionPlayerStatusShape),
    newRoundIndex: z.number(),
});
export const serverTurnPlayedMessageShape = baseServerMessageShape.extend({
    type: z.literal('turnPlayed'),
    roundIndex: z.number(),
    turn: z.custom((v) => {
        return z
            .object({
            playerId: idShapes.User,
            /**
             * Note: turn data for the current round is not public, which is
             * basically how this message will be used. But 'data' is possibly
             * a placeholder for future use if real-time turn information is
             * ever supported? idk.
             */
            data: z.any(),
        })
            .parse(v);
    }),
});
export const serverStatusChangeMessageShape = baseServerMessageShape.extend({
    type: z.literal('statusChange'),
    status: gameStatusShape,
});
/**
 * Sent during pregame if a different game is chosen. All game
 * state must be re-initialized. Use the HTTP API to fetch new
 * data.
 */
export const serverGameChangeMessageShape = baseServerMessageShape.extend({
    type: z.literal('gameChange'),
});
export const serverGameMembersChangeMessageShape = baseServerMessageShape.extend({
    type: z.literal('membersChange'),
    members: z.array(z.object({
        id: idShapes.User,
        displayName: z.string(),
        color: z.enum(colorNames),
    })),
});
export const serverGameStartingMessageShape = baseServerMessageShape.extend({
    type: z.literal('gameStarting'),
    startsAt: z.string().describe('ISO date string when the game starts'),
});
export const serverPlayerVoteForGameMessageShape = baseServerMessageShape.extend({
    type: z.literal('playerVoteForGame'),
    playerId: idShapes.User,
    votes: z.record(z.array(idShapes.User)).describe('Votes keyed by game ID'),
});
// general-purpose ack for client messages
export const serverAckMessageShape = baseServerMessageShape.extend({
    type: z.literal('ack'),
});
export const serverErrorMessageShape = baseServerMessageShape.extend({
    type: z.literal('error'),
    message: z.string(),
    code: z.number().optional(),
});
export const serverNextRoundScheduledMessageShape = baseServerMessageShape.extend({
    type: z.literal('nextRoundScheduled'),
    nextRoundCheckAt: z.string().describe('ISO date string'),
});
export const serverMessageShape = z.discriminatedUnion('type', [
    serverPlayerStatusChangeMessageShape,
    serverChatMessageShape,
    serverAckMessageShape,
    serverRoundChangeMessageShape,
    serverErrorMessageShape,
    serverStatusChangeMessageShape,
    serverTurnPlayedMessageShape,
    serverGameChangeMessageShape,
    serverGameMembersChangeMessageShape,
    serverNextRoundScheduledMessageShape,
    serverPlayerVoteForGameMessageShape,
    serverGameStartingMessageShape,
    serverPongMessageShape,
]);
const baseClientMessageShape = z.object({
    messageId: z.string(),
});
export const clientPingMessageShape = z.object({
    type: z.literal('ping'),
});
export const clientSendChatMessageShape = baseClientMessageShape.extend({
    type: z.literal('sendChat'),
    message: gameSessionChatInitShape,
});
export const clientSubmitTurnMessageShape = baseClientMessageShape.extend({
    type: z.literal('submitTurn'),
    turnData: z.record(z.unknown()),
});
export const clientRequestChatMessageShape = baseClientMessageShape.extend({
    type: z.literal('requestChat'),
    nextToken: z.string().nullable().optional(),
    sceneId: z.string().nullable(),
});
// only works in dev mode
export const clientResetGameMessageShape = baseClientMessageShape.extend({
    type: z.literal('resetGame'),
});
export const clientToggleChatReactionMessageShape = baseClientMessageShape.extend({
    type: z.literal('toggleChatReaction'),
    chatMessageId: idShapes.ChatMessage,
    reaction: z.string(),
    isOn: z.boolean(),
});
export const clientVoteForGameMessageShape = baseClientMessageShape.extend({
    type: z.literal('voteForGame'),
    gameId: z.string(),
    remove: z
        .boolean()
        .optional()
        .default(false)
        .describe('If true, this will remove the vote for the game instead of adding it.'),
});
export const clientDisconnectingMessageShape = baseClientMessageShape.extend({
    type: z.literal('disconnecting'),
});
export const clientGreetingMessageShape = baseClientMessageShape.extend({
    type: z.literal('greeting'),
});
export const clientMessageShape = z.discriminatedUnion('type', [
    clientSendChatMessageShape,
    clientSubmitTurnMessageShape,
    clientRequestChatMessageShape,
    clientResetGameMessageShape,
    clientToggleChatReactionMessageShape,
    clientVoteForGameMessageShape,
    clientDisconnectingMessageShape,
    clientGreetingMessageShape,
]);
//# sourceMappingURL=protocol.js.map